// suggestion service 集中处理 AI Agent 给到的 AI_OPS

import {
  embedSyncedDocMiddleware,
  MarkdownAdapter,
  titleMiddleware,
} from '@blocksuite/affine/shared/adapters';
import {
  copySelectedModelsCommand,
  draftSelectedModelsCommand,
  getBlockSelectionsCommand,
  getImageSelectionsCommand,
  getSelectedModelsCommand,
  getTextSelectionCommand,
} from '@blocksuite/affine/shared/commands';
import { BlockSelection, type EditorHost } from '@blocksuite/affine/std';
import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import {
  BaseSelection,
  // Block,
  type BlockModel,
  nanoid,
  Slice,
} from '@blocksuite/affine/store';
import { Service } from '@toeverything/infra';

import { getContentFromSlice } from '../../../blocksuite/utils/markdown-utils';
import type { DocService } from '../../doc';
import type { EditorService } from '../../editor';
import type {
  ChatAgentCommand,
  ChatAgentOps,
  DeleteOps,
  EditorContentInfo,
  InsertOps,
  ReplaceOps,
} from '../types/chat-message';
// import { removeSuggestionPanel } from './suggesiton-view-dom';
import { SuggestionManager } from './suggestion-manager';
import { injectSuggestionStyles as injectNewSuggestionStyles } from '../styles/suggestion-styles';
import { judgeSelectionIsEmpty } from './selection-parse';
import {
  type SuggestionType,
  type Suggestion,
  SuggestionStatus,
  type LLMBaseDataSnapshot,
} from '../types/suggestion.d';
import {
  BlockMarkdownLineNumberResolver,
  type BlockLineNumberInfo,
} from './block-markdown-line-resolver';
import {
  removeBeforeFirstLineBreak,
  removeBlockFromSnapshot,
} from '../utils/chat-ops-parse';
import { diffMarkdown, groupSuggestion } from './markdown-suggestion-diff';

import debounce from 'lodash-es/debounce';
import type {
  SuggestionStoreBeta,
  BlockMarkdownMap,
} from '../stores/suggestion-store-beta';
import type {
  AgentBetaStore,
  ComplexRecordType,
} from '../stores/agent-store-beta';
import {
  ViewportElementExtension,
  ViewportElementProvider,
} from '@blocksuite/affine-shared/services';
import { DocModeProvider } from '@blocksuite/affine-shared/services';

import type { AgentStoreService } from './agent-store-service';
import { uniqueId } from 'lodash-es';

export class SuggestionServiceBeta extends Service {
  processChatCommand: (
    agentContent: ChatAgentCommand<ComplexRecordType[]> & {
      title_new_value?: string;
    }
  ) => void;
  toggleSuggestion: (docId: string, uuid: string, isForceOverwrite: boolean) => void;

  // 🔑 新增：集成新的建议管理器（延迟初始化）
  private suggestionManager: SuggestionManager | null = null;
  // private allOperationButtonManager: AllOperationButtonManager | null = null;
  private _subscriptions: Array<{ unsubscribe: () => void }> = [];
  private _eventListeners: Array<{
    target: EventTarget;
    type: string;
    handler: EventListener;
  }> = [];
  private readonly _sugServiceId: string = '';
  private _disposed: boolean = false;

  /**
   * 公开的 service id，用于 AgentBetaStore 识别实例
   * 解决竞态条件：旧实例 dispose 时不会误清除新实例
   */
  get serviceId(): string {
    return this._sugServiceId;
  }

  // 🔑 新增：ViewportElementProvider 管理
  private viewportProviderCache: WeakMap<EditorHost, any> = new WeakMap();
  private isViewportExtensionRegistered = false;

  // 🔑 新增：用于保存当前的 suggestion watcher subscription
  private _suggestionWatcherSub: { unsubscribe: () => void } | null = null;

  constructor(
    private readonly docService: DocService,
    private readonly editorService: EditorService,
    private readonly suggestionStore: SuggestionStoreBeta,
    private readonly agentStore: AgentBetaStore,
    private readonly agentStoreService: AgentStoreService
  ) {
    super();

    this._disposed = false;
    this._sugServiceId = `SS-${Date.now()}`;

    // 注入新的 suggestion 样式
    injectNewSuggestionStyles(); // 注入新样式

    // 启动 suggestion 状态监听
    this.initializeSuggestionWatcher();

    // @typescript-eslint/no-misused-promises
    this.processChatCommand = this._processChatCommand;
    // @typescript-eslint/no-misused-promises
    this.toggleSuggestion = debounce(this._toggleSuggestion.bind(this), 300);

    // Extend Window interface for debugging
    (window as any).__suggestionIns = this;
  }

  /**
   * 当文档page切换后，调用该函数进行初始化。
   * 1、从agentStore中获取该文档的记录，并进行处理。
   * 2、清除该文档的suggestionState状态。
   * 3、设置该文档的suggestionService 实例。
   * 4、将 commands 获取计算 suggestion，并进行处理。
   * 5、监听实时 command 变化，并进行处理。
   */
  public async _docInitialized() {
    // 等待doc加载清理函数执行完成
    // await new Promise(resolve => setTimeout(resolve, 1000));

    //  TODO dispose后先跳过下面的执行， 后续优化前面的setTimeout(resolve, 1000)
    if (this._disposed) return;

    this._getSuggestionManager();
    this.initBlockSuggestionState();

    // 设置 agentStore 的 suggestionService 实例
    this.agentStore.setSuggestionService(this);

    const docId = this.docService.doc?.meta$.getValue().id || '';

    // // 获取该文档的记录
    const record = this.agentStore.getRecordByDocId(docId);

    if (!record) {
      return;
    }

    this.suggestionStore.resetData();

    const { commands, docBaseDataSnapshot } = record;

    if (['create', 'complex'].includes(record.type)) {
      if (docBaseDataSnapshot.blockLineMapping.length) {
        this.suggestionStore.replaceLLMBaseDataSnapshot(docBaseDataSnapshot);
      } else {
        const docsna = await this.getEditorContent();
        if (docsna) {
          this.suggestionStore.replaceLLMBaseDataSnapshot(
            docsna.docBaseDataSnapshot
          );
          // ✅ 使用 agentStoreService 更新 DB
          this.agentStoreService
            .updateDocBaseDataSnapshot({
              doc_id: docId,
              docBaseDataSnapshot: docsna.docBaseDataSnapshot,
            })
            .catch(e => {
              console.error('Error updating docBaseDataSnapshot:', e);
            });
        }
      }
    } else {
      this.suggestionStore.replaceLLMBaseDataSnapshot(docBaseDataSnapshot);
    }

    if (commands.length > 0) {
      this.processChatCommand({
        doc_id: docId,
        uuid: record.conversion_id,
        command: commands,
      });
    }
  }

  /**
   * ✅ 重构：从 ySuggestionInfo 恢复状态
   * - 检查 blocks 是否存在
   * - 如果存在：什么都不做，等 Manager observe 触发 UI 更新
   * - 如果不存在：数据不一致，清理
   */
  initBlockSuggestionState() {
    const suggestions = this._getAllBlockSuggestions();
    if (suggestions.length === 0) {
      console.log(
        `[suggestion] [${this._sugServiceId}] ySuggestionInfo 为空，无需恢复`
      );
      return;
    }

    console.log(
      `[suggestion] [${this._sugServiceId}] 从 ySuggestionInfo 恢复状态: ${suggestions.length} suggestions`
    );

    // ✅ 检查 blocks 是否存在
    const allBlocksExist = this._checkBlocksExist(suggestions);

    if (allBlocksExist) {
      // ✅ Blocks 存在，状态一致
      console.log(
        `[suggestion] [${this._sugServiceId}] Blocks 存在，状态一致，等待 Manager observe 触发 UI`
      );
      // Manager 的 observe 会自动处理 UI 更新，这里不需要做任何操作
    } else {
      // ❌ Blocks 不存在，数据不一致
      console.warn(
        `[suggestion] [${this._sugServiceId}] 数据不一致：ySuggestionInfo 存在但 blocks 不存在，清理`
      );
      this._cleanupInconsistentState();
    }
  }

  /**
   * ✅ 新增：检查 blocks 是否存在
   */
  private _checkBlocksExist(suggestions: Suggestion[]): boolean {
    const store = this.docService.doc?.blockSuiteDoc;
    if (!store) return false;

    return suggestions.every(s => {
      // 检查 newBlockIds 是否都存在
      if (s.newBlockIds && s.newBlockIds.length > 0) {
        return s.newBlockIds.every(blockId => {
          const block = store.getBlock(blockId);
          return block !== null;
        });
      }
      // 如果没有 newBlockIds，检查 targetBlockIds
      if (s.targetBlockIds && s.targetBlockIds.length > 0) {
        return s.targetBlockIds.every(blockId => {
          const block = store.getBlock(blockId);
          return block !== null;
        });
      }
      return true;
    });
  }

  /**
   * ✅ 新增：清理不一致状态
   */
  private _cleanupInconsistentState() {
    // 1. 清理 blocks 的 suggestionState
    this.clearDirtyBlocks();

    // 2. ✅ 通过 Manager 清理 ySuggestionInfo（唯一修改入口）
    const manager = this._getSuggestionManager();
    manager
      .clearAllSuggestions()
      .then(() => {
        console.log(
          `[suggestion] [${this._sugServiceId}] ySuggestionInfo 已清理`
        );
      })
      .catch(error => {
        console.error(
          `❌ [${this._sugServiceId}] 清理 ySuggestionInfo 失败:`,
          error
        );
      });
  }

  /**
   * 🔧 清除文档中残留的脏块（dirty blocks）
   * 处理 suggestion 状态残留的块：
   * - 'new' 类型的块：删除整个块
   * - 'old' 类型的块：清除 suggestionState
   */
  clearDirtyBlocks(_suggestions?: Suggestion[]): void {
    try {
      const store = this.docService.doc?.blockSuiteDoc;
      if (!store) {
        console.warn(
          '[suggestion] Store not available for clearing dirty blocks'
        );
        return;
      }
      // 获取所有块模型
      const allBlocks = store.blocks.value || {};

      // console.log('debuglz 开始清理脏块，共找到块数量:', Object.keys(allBlocks).length);
      Object.values(allBlocks).forEach(block => {
        try {
          const suggestionState = block.yBlock?.get('suggestionState') as
            | { type: 'new' | 'old' }
            | undefined;

          if (suggestionState) {
            if (suggestionState.type === 'new') {
              console.log('[suggestion] debuglz 处理块new:', block.id); // 🔍 获取并输出每个block的blockId
              store.deleteBlock(block.id);
            } else if (suggestionState.type === 'old') {
              block.yBlock?.delete('suggestionState');
              console.log('[suggestion] debuglz 处理块old:', block.id); // 🔍 获取并输出每个block的blockId
            }
          }
        } catch (blockError) {
          console.error('debuglz 处理单个块时出错:', block.id, blockError);
        }
      });
      // 同步文档状态
      store.captureSync();
    } catch (error) {
      console.error('❌ 清除脏块失败:', error);
    }
  }
  /**
   * 🔍 获取当前文档中所有block的ySuggestionState数据
   */
  private _getAllBlockSuggestions(): Array<Suggestion> {
    try {
      // 从 Y Doc 的 ySuggestionInfo 中获取保存的 suggestions
      const store = this.docService.doc?.blockSuiteDoc;
      if (!store) {
        console.warn('Store not available');
        return [];
      }

      const ySuggestionInfo = store.ySuggestionInfo;
      const suggestions = this._getSuggestionList(ySuggestionInfo);

      console.log(
        '[suggestion] debuglz 从 YDoc 获取到 suggestions:',
        suggestions
      );
      return suggestions;
    } catch (error) {
      console.error(
        '[suggestion] debuglz 获取 Y Doc 中的 suggestions 失败:',
        error
      );
      return [];
    }
  }

  /**
   * �🔧 从 ySuggestionInfo 中获取 list 数组
   */
  private _getSuggestionList(ySuggestionInfo: any): any[] {
    const data = ySuggestionInfo.get('list');
    return data?.list || [];
  }

  /**
   * ✅ 新增：统一从 ySuggestionInfo 读取 suggestions（只读层）
   * 用于替代 suggestionStore.getAllPendingSuggestionByDocId()
   */
  private _getAllYSuggestions(filter?: {
    status?: SuggestionStatus;
  }): Suggestion[] {
    try {
      const store = this.docService.doc?.blockSuiteDoc;
      if (!store) {
        return [];
      }

      const ySuggestionInfo = store.ySuggestionInfo;
      const listData = ySuggestionInfo.get('list');
      const allSuggestions: Suggestion[] = listData?.list || [];

      if (!filter) {
        return allSuggestions;
      }

      // 应用过滤条件
      return allSuggestions.filter(s => {
        if (filter.status && s.status !== filter.status) {
          return false;
        }
        return true;
      });
    } catch (error) {
      console.error('Failed to read from ySuggestionInfo:', error);
      return [];
    }
  }

  /**
   * ✅ 新增：从 ySuggestionInfo 获取所有 pending suggestions
   */
  private _getPendingSuggestions(): Suggestion[] {
    return this._getAllYSuggestions({ status: SuggestionStatus.Pending });
  }

  /**
   * 🔑 获取建议统计信息（用于调试）
   */
  getSuggestionStats(): any {
    try {
      const manager = this._getSuggestionManager();
      return manager.getSuggestionStats();
    } catch (error) {
      console.error('获取建议统计失败:', error);
      return { totalBlocks: 0, suggestionsActive: 0, buttonsActive: 0 };
    }
  }

  /**
   * 🔑 安全地获取或初始化建议管理器
   */
  private _getSuggestionManager(): SuggestionManager {
    if (!this.suggestionManager) {
      try {
        this.suggestionManager = new SuggestionManager(
          this.docService,
          this.editorService
        );
        this._setupNewSuggestionManagerEvents();
        console.log('[suggestion] SuggestionManager initialized successfully');
      } catch (error) {
        console.error('Failed to initialize SuggestionManager:', error);
        throw error;
      }
    }
    return this.suggestionManager;
  }

  /**
   * 🔑 新增：设置新建议管理器的事件监听
   */
  private _setupNewSuggestionManagerEvents(): void {
    // 监听建议操作事件
    if (typeof document !== 'undefined') {
      const handler = ((e: CustomEvent) => {
        const { suggestionId, action } = e.detail;

        if (action === 'accept') {
          this._getSuggestionManager()
            .applySuggestion(suggestionId)
            .catch(console.error);
          // 同步到原有的store系统
          // this._syncAcceptToStore(suggestionId);
        } else if (action === 'reject') {
          this._getSuggestionManager()
            .rejectSuggestion(suggestionId)
            .catch(console.error);
          // 同步到原有的store系统
          // this._syncRejectToStore(suggestionId);
        }
      }) as EventListener;

      document.addEventListener('suggestion-action', handler);
      // ✅ 保存引用以便清理
      this._eventListeners.push({
        target: document,
        type: 'suggestion-action',
        handler,
      });
    }
  }

  // ✅ 实现 dispose 方法
  override dispose(): void {
    console.log(
      `[suggestion] [${this._sugServiceId}] Disposing SuggestionServiceBeta`
    );

    this._disposed = true;

    // 1. 取消当前批次
    if (this.suggestionManager) {
      this.suggestionManager.cancelCurrentBatch();
    }

    // 2. 取消所有订阅
    this._subscriptions.forEach(sub => {
      try {
        sub.unsubscribe();
      } catch (error) {
        console.error('Failed to unsubscribe:', error);
      }
    });
    this._subscriptions = [];

    // 3. 移除所有事件监听器
    this._eventListeners.forEach(({ target, type, handler }) => {
      try {
        target.removeEventListener(type, handler);
      } catch (error) {
        console.error('Failed to remove event listener:', error);
      }
    });
    this._eventListeners = [];

    // 4. 销毁 SuggestionManager
    if (this.suggestionManager) {
      this.suggestionManager.dispose();
      this.suggestionManager = null;
    }

    this.clearViewportProviderCache();

    // 6. 清理 suggestion watcher
    if (this._suggestionWatcherSub) {
      this._suggestionWatcherSub.unsubscribe();
      this._suggestionWatcherSub = null;
    }

    // 7. 清理 agentStore 中的引用 (防止内存泄漏)
    // ⚠️ 使用 clearSuggestionService 而非 setSuggestionService(null)
    // 避免竞态条件：旧实例 dispose 时不会误清除新实例
    this.agentStore.clearSuggestionService(this._sugServiceId);

    // 5. 调用父类的 dispose
    super.dispose();
  }

  /**
   * ✅ 重构：初始化 suggestion 状态监听器
   * - 只监听 editor 变化
   * - ❌ 移除 Store.signal$ 监听（改为 Manager observe 驱动）
   */
  private initializeSuggestionWatcher() {
    // 监听 editor 变化，当 editor 可用时启动监听
    this._subscriptions.push(
      this.editorService.editor.editorContainer$.subscribe(editorContainer => {
        // ✅ 先清理旧的 subscription
        if (this._suggestionWatcherSub) {
          this._suggestionWatcherSub.unsubscribe();
          this._suggestionWatcherSub = null;
        }

        if (editorContainer?.host) {
          console.log(`📝 [${this._sugServiceId}] Editor 就绪`);
          this._docInitialized().catch(() => {});
          this._suggestionWatcherSub = this.startSuggestionWatcher(
            editorContainer.host
          );
        }
      })
    );
    // 监听 selection 变化
  }

  private async _LLMCommandParse(commands: ComplexRecordType[]) {
    // 每次只取最后一条进行处理（最新）
    const mds = commands.at(-1)?.snap_content || '';

    const adapter = new MarkdownAdapter(
      this.docService.doc.blockSuiteDoc.getTransformer([
        titleMiddleware(
          this.docService.doc.blockSuiteDoc.workspace.meta.docMetas
        ),
        embedSyncedDocMiddleware('content'),
      ]),
      this.docService.doc.blockSuiteDoc.provider
    );

    // const formattedMarkdown = await adapter.formatMarkdown(mds);

    console.log('[suggestion] LLMCommandParse-formattedMarkdown', mds);

    this.suggestionStore.modifyLLMMarkdown({
      markdown: mds,
    });
  }

  private async _toggleSuggestion(docId: string, uuid: string, isForceOverwrite: boolean) {
    const currentMarkdown = await this._getCurrentMarkdown('defaultReject');
    const markdown =
      this.suggestionStore.LLMBaseDataSnapshot$.getValue().markdown;

    console.log(
      '[suggestion] toggleSuggestion',
      '------->>>>\n\n\n',
      currentMarkdown?.file,
      '------->>>>\n\n\n',
      markdown
    );
    const diffResult = diffMarkdown(currentMarkdown?.file || '', markdown);

    console.log('[suggestion] LLMCommandParse-diffResult', diffResult.changes);

    const changes = diffResult.changes.filter(item => {
      if (item.type === 'removed' && item.value.length === 0 && !item.block) {
        return false;
      }

      return true;
    });

    const suggestions = groupSuggestion(changes);

    console.log('[suggestion] LLMCommandParse-suggestions', suggestions);

    // ✅ Sandbox 会自动处理旧批次删除，不需要手动调用

    const patchGroup = await this.generateSuggestion({
      uuid: uuid,
      doc_meta: {
        id: docId,
        title: '',
      },
      doc_id: docId,
      command: suggestions.map(item => {
        const baseCommand = {
          selection_str: item.selection_str,
          new_str: item.new_str,
          start_line_number: item.start_line_number,
          end_line_number: item.end_line_number,
          id: nanoid(),
          timestamp: Date.now(),
          canvas_id: docId,
        };

        // 根据操作类型创建正确的命令对象
        if (item.op === 'replace') {
          return { ...baseCommand, op: item.op } as ReplaceOps;
        } else if (item.op === 'insert') {
          return { ...baseCommand, op: item.op } as InsertOps;
        } else if (item.op === 'delete') {
          return { ...baseCommand, op: item.op } as DeleteOps;
        }

        return baseCommand as any;
      }),
    });

    console.log(
      '[suggestion] LLMCommandParse-patchGroup',
      patchGroup?.suggestions
    );

    if (!patchGroup) {
      return;
    }

    if (patchGroup?.suggestions.length > 0) {
      // ✅ 重构：直接调用 Manager 创建 suggestions
      // 不再通过 Store.addSuggestion() → Store.signal$ 触发
      console.log(
        `[suggestion] [${this._sugServiceId}] 通过 Manager 创建 ${patchGroup.suggestions.length} suggestions`
      );

      const manager = this._getSuggestionManager();
      const results = await manager
        .batchCreateSuggestions(patchGroup.suggestions)
        .catch(error => {
          console.error(
            `❌ [${this._sugServiceId}] 创建 suggestions 失败:`,
            error
          );
          return new Map();
        });

      if (isForceOverwrite) {
        await manager.applyAllSuggestions(docId);
      }

      // ✅ 创建成功后，立即清理 agent-store
      // 这样 _docInitialized 就不会重复处理
      if (results.size > 0) {
        console.log(
          `[suggestion] [${this._sugServiceId}] Suggestions 创建成功，清理 agent-store record`
        );
        // ✅ 使用 agentStoreService 从 DB 删除
        await this.agentStoreService.removeRecordByDocId(docId);
      }
    } else {
      // 未 diff 出差异，移除检查后的 command
      await this.agentStoreService.removeRecordByDocId(docId);
    }
  }

  private async _getCurrentMarkdown(
    filterStrategy: 'defaultAccept' | 'defaultReject'
  ) {
    const blockSuiteDoc = this.docService.doc?.blockSuiteDoc;
    if (!blockSuiteDoc) {
      return;
    }

    // ✅ 从 ySuggestionInfo 读取（单一数据源）
    const pendingSuggestions = this._getPendingSuggestions();
    const filterBlockIDs: string[] = [];
    if (filterStrategy === 'defaultAccept') {
      pendingSuggestions.forEach(item => {
        if (item.type !== 'insert') {
          filterBlockIDs.push(...(item.targetBlockIds || []));
        }
      });
    } else if (filterStrategy === 'defaultReject') {
      pendingSuggestions.forEach(item => {
        filterBlockIDs.push(...(item.newBlockIds || []));
      });
    }

    // Create transformer for document conversion
    const transformer = blockSuiteDoc.getTransformer([
      titleMiddleware(blockSuiteDoc.workspace.meta.docMetas),
      embedSyncedDocMiddleware('content'),
    ]);

    const snapshot = transformer.docToSnapshot(blockSuiteDoc);
    const adapter = new MarkdownAdapter(transformer, blockSuiteDoc.provider);

    if (!snapshot) {
      return;
    }

    const snapshotWithoutNewBlocks = removeBlockFromSnapshot(
      snapshot,
      filterBlockIDs
    );

    const blockLineMapping = await adapter.fromBlockSnapshotWithLineMap({
      snapshot: snapshotWithoutNewBlocks?.blocks,
      assets: transformer.assetsManager,
    });

    if (!blockLineMapping) {
      return;
    }

    // 剔除标题
    const title = snapshot.meta.title;

    if (title?.length > 0) {
      blockLineMapping.file = removeBeforeFirstLineBreak(blockLineMapping.file);
    }

    console.log(
      '[suggestion] getCurrentMarkdown-blockLineMapping',
      JSON.stringify(blockLineMapping),
      JSON.stringify(snapshotWithoutNewBlocks)
    );

    return {
      ...blockLineMapping,
      snapshot: snapshotWithoutNewBlocks,
    };
  }

  setEditorDisplaySelections(selections: BaseSelection[]) {
    const editor = this.editorService.editor;
    const editorContainer = editor.editorContainer$.value;
    const host = editorContainer?.host;
    if (host) {
      host.std.selection.setDisplaySelections(selections);
    }
  }

  /**
   * 启动 suggestion 状态监听器
   */
  private startSuggestionWatcher(host: EditorHost) {
    // 获取 store
    const selection = host.std.selection;

    const debounceHandle = debounce(async (event: any) => {
      // 1、处理数
      console.log('######--startSuggestionWatcher-event  ', this._sugServiceId);
      const isEmpty = judgeSelectionIsEmpty(event);

      const selectionChangedSubscribe =
        this.agentStore.selectionChangedSubscribe;

      if (isEmpty) {
        this.setEditorDisplaySelections([]);
        selectionChangedSubscribe.forEach(cb => {
          try {
            cb({
              selectedModels: [],
              selectionMarkdown: '',
              timestamp: Date.now(),
            });
          } catch (error) {
            console.log(error);
          }
        });
      } else {
        try {
          const [success, ctx] = host.std.command
            .chain()
            .pipe(getSelectedModelsCommand)
            .run();

          if (success && ctx?.selectedModels?.length) {
            const { selectedModels } = ctx;

            const rootDom = document.querySelector(
              '[class="affine-block-children-container"]'
            );
            const selectedFirstBlockID = event[0].blockId || null;

            if (rootDom && selectedFirstBlockID) {
              try {
                if (host) {
                  // Create slice from selected models
                  // 2. 创建transformer
                  const transformer = host.std.store.getTransformer();
                  // 3. 创建markdown adapter
                  const markdownAdapter = new MarkdownAdapter(
                    transformer,
                    host.std.store.provider
                  );
                  const bsDoc = this.docService.doc?.blockSuiteDoc;
                  const snapshot = bsDoc
                    ? transformer.docToSnapshot(bsDoc)
                    : null;

                  if (snapshot) {
                    const lines = await markdownAdapter.fromBlock2LineMap({
                      snapshot,
                      blockList: selectedModels.map(model => model.id),
                      assets: transformer.assetsManager,
                    });

                    if (lines.blockMap.length) {
                      const startLineNumber =
                        lines.blockMap[0]?.startLine - 2 || 0;

                      let selectionMarkdown = lines.blockMap.reduce(
                        (acc, item) => {
                          return acc + '\n\n' + item.markdown;
                        },
                        ''
                      );

                      // 去除首行和末行
                      selectionMarkdown = selectionMarkdown.trim();

                      this.setEditorDisplaySelections(event);
                      selectionChangedSubscribe.forEach(cb => {
                        cb({
                          selectedModels,
                          selectionMarkdown: selectionMarkdown || '',
                          timestamp: Date.now(),
                          start_line_number: startLineNumber || 1,
                        });
                      });
                    }
                  }
                }
              } catch (error) {
                console.error('Failed to get selection markdown:', error);
              }
            }
          }
        } catch (error) {
          console.error('Failed to execute selection changed callback:', error);
        }
      }
    }, 300);

    const subscription = selection.slots.changed.subscribe(debounceHandle);

    return subscription;
  }

  /**
   * 安全地获取 EditorHost
   */
  private getEditorHost(): EditorHost | null {
    const editor = this.editorService.editor;
    const editorContainer = editor.editorContainer$.value;
    return editorContainer?.host || null;
  }

  private async getViewportInfo(): Promise<number[]> {
    // 获取视口内的所有块
    const blocks = this.getViewportBlocks();
    const editorHost = this.getEditorHost();
    const editorDoc = this.docService.doc?.blockSuiteDoc;

    // 提前返回以减少嵌套与无效计算
    if (!editorHost || blocks.length === 0) {
      return [0, 0];
    }

    if (!editorDoc) {
      return [0, 0];
    }

    // 创建 transformer 与 markdown adapter
    const transformer = editorHost.std.store.getTransformer();
    const snapshot = transformer.docToSnapshot(editorDoc);
    if (!snapshot) {
      // 转换失败或得到空快照，返回默认区间
      return [0, 0];
    }
    const markdownAdapter = new MarkdownAdapter(
      transformer,
      editorHost.std.store.provider
    );

    const blockIds = blocks.map(b => b.id);

    const lines = await markdownAdapter.fromBlock2LineMap({
      snapshot,
      blockList: blockIds,
      assets: transformer.assetsManager,
    });

    let minStart = Infinity;
    let maxEnd = -Infinity;
    let hasBlock = false;

    for (const block of lines.blockMap as Array<BlockMarkdownMap | null>) {
      if (!block) continue;
      hasBlock = true;
      const start = block.startLine - 2;
      const end = block.endLine - 2;

      if (start < minStart) minStart = start;
      if (end > maxEnd) maxEnd = end;
    }

    if (minStart === Infinity) {
      minStart = -1;
    }
    if (maxEnd === -Infinity) {
      maxEnd = -1;
    }

    if (!hasBlock) {
      return [-1, -1];
    }

    return [minStart, maxEnd];
  }

  /**
   * ✅ 批量创建 suggestion（使用沙箱方案）
   * 2025-12-16 注释掉，后续下线这个api
   */
  // async batchCreateSuggestion(params: {
  //   suggestions: Suggestion[];
  //   batch_id: string;
  //   doc_id: string;
  // }): Promise<Map<string, any>> {
  //   try {
  //     const doc = this.docService.doc;
  //     const docId = doc?.meta$.getValue().id || '';

  //     if (!doc || params.doc_id !== docId) {
  //       throw new Error('DocId not found or mismatch');
  //     }

  //     // 过滤出需要创建的 suggestions
  //     const createSuggestions = params.suggestions.filter(
  //       item => item.status === SuggestionStatus.Initial
  //     );

  //     if (createSuggestions.length === 0) {
  //       console.log(
  //         `ℹ️ [${this._sugServiceId}] 批次 ${params.batch_id} 无需创建的 suggestion`
  //       );
  //       return new Map();
  //     }

  //     console.log(
  //       `🔄 [${this._sugServiceId}] 批量创建: ${createSuggestions.length} suggestions (batch: ${params.batch_id})`
  //     );

  //     // ✅ 使用 SuggestionManager 的沙箱方案（带中断机制）
  //     const manager = this._getSuggestionManager();
  //     const results = await manager.batchCreateSuggestions(createSuggestions);

  //     if (results.size === 0) {
  //       console.warn(
  //         `⚠️ [${this._sugServiceId}] 批次 ${params.batch_id} 创建失败或被中断`
  //       );
  //       return new Map();
  //     }

  //     console.log(
  //       `✅ [${this._sugServiceId}] 批次 ${params.batch_id} 创建完成: ${results.size} suggestions`
  //     );

  //     // 转换返回格式（保持兼容性）
  //     const formattedResults = new Map();
  //     for (const [suggestionId, result] of results.entries()) {
  //       formattedResults.set(suggestionId, {
  //         suggestionId: result.suggestionId || suggestionId,
  //         targetBlockIds: result.targetBlockIds || [],
  //         newBlockIds: result.newBlockIds || [],
  //         batch_id: result.batch_id || params.batch_id,
  //         doc_id: result.doc_id || params.doc_id,
  //         status:
  //           result.status === 'pending'
  //             ? SuggestionStatus.Pending
  //             : SuggestionStatus.Deleted,
  //       });
  //     }

  //     return formattedResults;
  //   } catch (error) {
  //     console.error(`❌ [${this._sugServiceId}] 批量创建失败:`, error);
  //     return new Map();
  //   }
  // }

  async _processChatCommand(
    agentContent: ChatAgentCommand<ComplexRecordType[]>
  ) {
    // 1. 获取当前编辑选中 & 全文信息
    const docId = this.docService.doc?.meta$.getValue().id || '';

    if (!docId || docId !== agentContent.doc_id) {
      console.error('SuggestionServiceBeta: DocId not found or not match');
      return;
    }

    const { isForceOverwrite } = agentContent.command[0] || { isForceOverwrite : false };

    await this._LLMCommandParse(agentContent.command);
    this.toggleSuggestion(docId, agentContent.uuid, isForceOverwrite);
  }

  /**
   * 🔧 取消当前批次
   */
  cancelCurrentBatch(): void {
    console.log(`⚠️ [${this._sugServiceId}] 取消当前批次`);
    const manager = this._getSuggestionManager();
    manager.cancelCurrentBatch();
  }

  /**
   * 🔍 获取当前批次状态
   */
  getCurrentBatchStatus(): {
    batchId: string | null;
    isProcessing: boolean;
  } {
    const manager = this._getSuggestionManager();
    return manager.getCurrentBatchStatus();
  }

  // 最新
  private async _findTargetBlocksByBlockLineMapping(params: {
    selection_str?: string;
    start_line_number: number;
    end_line_number: number;
    llmBaseDataSnapshot: LLMBaseDataSnapshot;
  }) {
    const { llmBaseDataSnapshot } = params;
    const { blockLineMapping } = llmBaseDataSnapshot;

    let start_line_number =
      params.start_line_number <= 2 ? 3 : params.start_line_number;
    let end_line_number =
      params.end_line_number <= 2 ? 3 : params.end_line_number;

    const targetBlockIds: string[] = [];

    // 处理空文档插入首行的情况：
    // 1. blockLineMapping 为空或未定义
    // 2. 所有 block 的 markdown 内容都为空（空文档可能包含空的 note/paragraph 节点）
    const isEmptyDocument =
      !blockLineMapping ||
      blockLineMapping.length === 0 ||
      blockLineMapping.every(
        item => !item.markdown || item.markdown.trim() === ''
      );

    if (isEmptyDocument && params.start_line_number <= 2) {
      const store = this.docService.doc?.blockSuiteDoc;
      if (store) {
        // 从文档中查找第一个 note block，然后获取其第一个子节点
        const rootBlock = store.root;
        if (rootBlock) {
          const noteBlock = rootBlock.children?.find(
            child => child.flavour === 'affine:note'
          );
          if (noteBlock) {
            const firstChild = noteBlock.children?.[0];
            if (firstChild) {
              targetBlockIds.push(firstChild.id);
            } else {
              // note 为空，使用 note 本身
              targetBlockIds.push(noteBlock.id);
            }
          }
        }
      }
      return { targetBlockIds };
    }

    // try {
    //   blockLineMapping.shift();
    // } catch (error) {}

    function findStartBlock(start_line_number: number) {
      let startBlock: BlockMarkdownMap | undefined = blockLineMapping.find(
        item => {
          return (
            item.startLine <= start_line_number &&
            item.endLine >= start_line_number
          );
        }
      );

      // 如果找不到对应的block（可能是空行），向前查找最近的block
      if (!startBlock) {
        const candidateBlocks = blockLineMapping
          .filter(item => item.endLine < start_line_number)
          .sort((a, b) => b.endLine - a.endLine);

        if (candidateBlocks.length > 0) {
          startBlock = candidateBlocks[0];
        }
      }

      return startBlock;
    }

    function findEndBlock(end_line_number: number) {
      let endBlock: BlockMarkdownMap | undefined = blockLineMapping.find(
        item => {
          return (
            item.startLine <= end_line_number && item.endLine >= end_line_number
          );
        }
      );

      if (!endBlock) {
        const candidateBlocks = blockLineMapping
          .filter(item => item.endLine < end_line_number)
          .sort((a, b) => b.endLine - a.endLine);

        if (candidateBlocks.length > 0) {
          endBlock = candidateBlocks[0];
        }
      }

      return endBlock;
    }

    const startBlock = findStartBlock(start_line_number);
    const endBlock = findEndBlock(end_line_number);

    if (!startBlock || !endBlock) {
      return {
        targetBlockIds,
      };
    }

    const startIndex = blockLineMapping.findIndex(
      item => item.blockId === startBlock?.blockId
    );
    const endIndex = blockLineMapping.findIndex(
      item => item.blockId === endBlock?.blockId
    );

    if (startIndex === -1 || endIndex === -1) {
      return {
        targetBlockIds,
      };
    }

    for (let i = startIndex; i <= endIndex; i++) {
      const item = blockLineMapping[i];
      if (item) {
        targetBlockIds.push(item.blockId);
      }
    }

    return {
      targetBlockIds,
    };
  }

  async generateSuggestion(agentContent: ChatAgentCommand<ChatAgentOps>) {
    const patchGroup: {
      batch_id: string;
      suggestions: Suggestion[];
    } = {
      batch_id: agentContent.uuid,
      suggestions: [],
    };

    const { command } = agentContent;

    const llmBaseDataSnapshot = await this._getCurrentMarkdown('defaultReject');

    const bsDoc = this.docService.doc?.blockSuiteDoc;
    if (!bsDoc) {
      return null;
    }

    const transformer = bsDoc.getTransformer([
      titleMiddleware(bsDoc.workspace.meta.docMetas),
      embedSyncedDocMiddleware('content'),
    ]);

    const snapshot = transformer.docToSnapshot(bsDoc);

    if (!llmBaseDataSnapshot) {
      return;
    }

    const offset = 2;

    console.log('####generateSuggestion', llmBaseDataSnapshot, bsDoc.doc.id);

    for (const item of command) {
      const { op } = item;

      const temep = JSON.parse(JSON.stringify(llmBaseDataSnapshot));

      const { targetBlockIds } = await this._findTargetBlocksByBlockLineMapping(
        {
          selection_str: item.selection_str,
          start_line_number: Number(item.start_line_number) + offset,
          end_line_number: Number(item.end_line_number) + offset,
          llmBaseDataSnapshot: {
            snapshot: snapshot || null,
            markdown: temep?.file,
            blockLineMapping: temep?.blockMap,
            docId: agentContent.doc_meta?.id || '',
          },
        }
      );

      if (!targetBlockIds?.length) {
        continue;
      }

      const host = this.getEditorHost();
      if (!host) {
        console.warn('EditorHost not available for replace operation');
        break;
      }

      switch (op) {
        case 'replace': {
          if (item.new_str.length > 0) {
            const suggestion = this._generateSuggestionItem({
              targetBlockIds,
              newBlocks: [],
              type: 'replace',
              chatCommand: item,
              batch_id: patchGroup.batch_id,
            });

            patchGroup.suggestions.push(suggestion);
          } else {
            // replace 中不存在 new_str 时，视为删除
            const suggestion = this._generateSuggestionItem({
              targetBlockIds,
              newBlocks: [],
              type: 'delete',
              chatCommand: item,
              batch_id: patchGroup.batch_id,
            });

            patchGroup.suggestions.push(suggestion);
          }

          break;
        }

        case 'delete': {
          const suggestion = this._generateSuggestionItem({
            targetBlockIds,
            newBlocks: [],
            type: 'delete',
            chatCommand: item,
            batch_id: patchGroup.batch_id,
          });

          patchGroup.suggestions.push(suggestion);
          break;
        }

        case 'insert': {
          if (item.new_str.length > 0) {
            const suggestion = this._generateSuggestionItem({
              targetBlockIds,
              newBlocks: [],
              type: 'insert',
              chatCommand: item,
              batch_id: patchGroup.batch_id,
            });

            patchGroup.suggestions.push(suggestion);
          }
          break;
        }
      }
    }

    return patchGroup;
  }

  async getEditorSelections() {
    // Access the global currentEditor (AffineEditorContainer)
    const editor = this.editorService.editor;
    const editorContainer = editor.editorContainer$.value;

    if (!editorContainer) {
      return { blockIds: [], elementIds: [] };
    }
    // Use the same logic as getSelectedNodes
    const std = editorContainer.std;
    const mode = editorContainer.mode;
    const blockIds: string[] = [];
    const elementIds: string[] = [];
    const result = { blockIds, elementIds };
    if (!std) {
      return result;
    }
    if (mode === 'edgeless') {
      const { selection } = std.get?.(GfxControllerIdentifier) ?? {};
      if (selection && Array.isArray(selection.selectedElements)) {
        for (const element of selection.selectedElements) {
          if (element?.id && element.constructor?.name?.includes('Block')) {
            blockIds.push(element.id);
          } else if (element?.id) {
            elementIds.push(element.id);
          }
        }
      }
      return result;
    }
    // For page mode, try to use the command chain if available
    if (std.command && typeof std.command.chain === 'function') {
      const [success, ctx] = std.command
        .chain()
        .tryAll((chain: any) => [
          chain.pipe(getTextSelectionCommand),
          chain.pipe(getBlockSelectionsCommand),
          chain.pipe(getImageSelectionsCommand),
        ])
        .pipe(getSelectedModelsCommand, {
          mode: 'highest',
        })
        .run();
      if (success && ctx.selectedModels?.length) {
        blockIds.push(...ctx.selectedModels.map((model: any) => model.id));
      }
    }

    // Fallback: if no blockIds were collected, try to infer from DOM selection
    if (!blockIds.length) {
      try {
        const host: any = editorContainer.host;
        const root: Document | ShadowRoot | null =
          host?.shadowRoot ?? host?.ownerDocument ?? document;
        const sel =
          (root as ShadowRoot | Document).getSelection?.() ??
          window.getSelection();
        const anchorNode = sel?.anchorNode;
        let el =
          anchorNode instanceof Element
            ? anchorNode
            : (anchorNode?.parentElement ?? null);
        while (el) {
          const blockId = el.getAttribute?.('data-block-id');
          if (blockId) {
            blockIds.push(blockId);
            break;
          }
          el = el.parentElement;
        }
      } catch (error) {
        console.warn('getEditorSelections fallback failed:', error);
      }
    }

    return result;
  }

  /**
   * 🔑 获取 ViewportElementProvider 实例
   * 确保正确的依赖注入、初始化验证、生命周期管理和边界情况处理
   */
  private getViewportElementProvider(): any | null {
    try {
      // 1. 获取当前编辑器主机
      const editorHost = this.getEditorHost();
      if (!editorHost?.std) {
        console.warn(
          'EditorHost or std not available for ViewportElementProvider'
        );
        return null;
      }

      // 2. 检查缓存
      if (this.viewportProviderCache.has(editorHost)) {
        return this.viewportProviderCache.get(editorHost);
      }

      // 3. 确保 ViewportElementExtension 已注册
      if (!this.isViewportExtensionRegistered) {
        this.registerViewportExtension(editorHost);
      }

      // 4. 获取 ViewportElementProvider 实例
      const viewportProvider = editorHost.std.get(ViewportElementProvider);

      // 5. 验证初始化
      if (!viewportProvider) {
        console.error('ViewportElementProvider not found after registration');
        return null;
      }

      // 6. 缓存实例
      this.viewportProviderCache.set(editorHost, viewportProvider);

      return viewportProvider;
    } catch (error) {
      console.error('Failed to get ViewportElementProvider:', error);
      return null;
    }
  }

  /**
   * 🔑 注册 ViewportElementExtension
   */
  private registerViewportExtension(editorHost: EditorHost): void {
    try {
      const std = editorHost.std;

      // 1. 根据当前模式选择视口选择器
      let selector = '.affine-page-viewport'; // 默认页面模式

      try {
        const docModeProvider = std.getOptional(DocModeProvider);
        const mode = docModeProvider?.getEditorMode?.() ?? 'page';
        selector =
          mode === 'edgeless'
            ? '.affine-edgeless-viewport'
            : '.affine-page-viewport';
      } catch (error) {
        console.warn(
          'Failed to get editor mode, using default page viewport:',
          error
        );
      }

      // 2. 注册扩展到 std.container
      ViewportElementExtension(selector).setup(std.container);

      this.isViewportExtensionRegistered = true;
      console.log(
        `ViewportElementExtension registered with selector: ${selector}`
      );
    } catch (error) {
      console.error('Failed to register ViewportElementExtension:', error);
      throw error;
    }
  }

  /**
   * 🔑 获取视口内的块列表（带边界情况处理）
   */
  private getViewportBlocks(): any[] {
    try {
      const viewportProvider = this.getViewportElementProvider();
      if (!viewportProvider) {
        console.warn('ViewportElementProvider not available');
        return [];
      }

      return viewportProvider.viewportBlocks || [];
    } catch (error) {
      console.error('Failed to get viewport blocks:', error);
      return [];
    }
  }

  /**
   * 🔑 清理 ViewportElementProvider 缓存（生命周期管理）
   */
  private clearViewportProviderCache(): void {
    try {
      // 清理缓存
      this.viewportProviderCache = new WeakMap();
      this.isViewportExtensionRegistered = false;
      console.log('ViewportElementProvider cache cleared');
    } catch (error) {
      console.error('Failed to clear ViewportElementProvider cache:', error);
    }
  }
  // 获取当前编辑选中 & 全文信息
  async getEditorContent(): Promise<
    EditorContentInfo & { docBaseDataSnapshot: LLMBaseDataSnapshot }
  > {
    const editor = this.editorService.editor;
    const doc = this.docService.doc;
    const editorContainer = editor.editorContainer$.value;
    const meta = doc.meta$.getValue();

    if (!editorContainer || !doc) {
      return {
        id: '',
        selection: {
          blockIds: [],
          elementIds: [],
          markdown: '',
          start_line_number: 0,
          viewport_line_range: [],
        },
        document: { markdown: '' },
        docBaseDataSnapshot: {
          snapshot: null,
          markdown: '',
          blockLineMapping: [],
          docId: '',
        },
      };
    }

    // viewport info
    const viewportLineRange = await this.getViewportInfo();

    // Get selection info
    const selectionInfo = await this.getEditorSelections();

    // Get selection markdown content
    let selectionMarkdown = '';
    let startLineNumber = -1;

    if (
      selectionInfo.blockIds.length > 0 ||
      selectionInfo.elementIds.length > 0
    ) {
      try {
        const host = editorContainer.host;
        if (host) {
          // Create slice from selected models
          // 2. 创建transformer
          const transformer = host.std.store.getTransformer();
          // 3. 创建markdown adapter
          const markdownAdapter = new MarkdownAdapter(
            transformer,
            host.std.store.provider
          );
          const bsDoc = this.docService.doc?.blockSuiteDoc;
          const snapshot = bsDoc ? transformer.docToSnapshot(bsDoc) : null;

          if (snapshot) {
            const lines = await markdownAdapter.fromBlock2LineMap({
              snapshot,
              blockList: selectionInfo.blockIds,
              assets: transformer.assetsManager,
            });

            if (lines.blockMap.length) {
              startLineNumber = lines.blockMap[0]?.startLine - 2 || 0;

              selectionMarkdown = lines.blockMap.reduce((acc, item) => {
                return acc + '\n\n' + item.markdown;
              }, '');

              // 去除首行和末行
              selectionMarkdown = selectionMarkdown.trim();
            }
          }
        }
      } catch (error) {
        console.error('Failed to get selection markdown:', error);
      }
    }

    // Get full document markdown
    let documentMarkdown = '';

    const llmBaseDataSnapshot: LLMBaseDataSnapshot = {
      snapshot: null,
      markdown: '',
      blockLineMapping: [],
      docId: meta.id || '',
    };

    try {
      const currentMarkdown = await this._getCurrentMarkdown('defaultAccept');

      if (currentMarkdown) {
        documentMarkdown = currentMarkdown.file;
        llmBaseDataSnapshot.snapshot = currentMarkdown.snapshot;
        llmBaseDataSnapshot.blockLineMapping = currentMarkdown.blockMap;
        llmBaseDataSnapshot.markdown = currentMarkdown.file;
      }
    } catch (error) {
      console.error('Failed to get document markdown:', error);
    }

    // 更新 LLM 基础数据
    this.suggestionStore.replaceLLMBaseDataSnapshot(llmBaseDataSnapshot);

    return {
      id: meta.id || '',
      meta: {
        title: meta.title || '',
      },
      selection: {
        blockIds: selectionInfo.blockIds,
        elementIds: selectionInfo.elementIds,
        markdown: selectionMarkdown,
        start_line_number: startLineNumber,
        viewport_line_range: viewportLineRange,
      },
      document: {
        markdown: documentMarkdown,
      },
      docBaseDataSnapshot: llmBaseDataSnapshot,
    };
  }

  private _generateSuggestionItem(params: {
    targetBlockIds: string[];
    newBlocks: BlockModel<object>[];
    type: SuggestionType;
    chatCommand: InsertOps | DeleteOps | ReplaceOps;
    batch_id: string;
  }): Suggestion {
    const { targetBlockIds, newBlocks } = params;
    return {
      id: nanoid(),
      batch_id: params.batch_id,
      chatCommand: params.chatCommand,
      type: params.type,
      targetBlockIds,
      newBlocksData: newBlocks,
      status: SuggestionStatus.Initial,
      createdAt: Date.now(),
    };
  }

  async copySelectedNodes(): Promise<boolean> {
    const host = this.getEditorHost();

    const std = host?.std;

    if (!host || !std) {
      console.error('EditorHost std not available');
      return false;
    }

    try {
      // 确保窗口获得焦点，这对于 clipboard API 是必需的
      // 特别是从 console 调用时，文档可能失去焦点
      window.focus();

      // 等待一小段时间确保焦点已经设置
      await new Promise(resolve => setTimeout(resolve, 50));

      // 获取所有 blocks
      const allBlocks = Object.values(std.store.blocks.peek());

      // 过滤出有效的 block（排除 root 等特殊 block）
      const validBlocks = allBlocks.filter(block => {
        // 通常我们只需要 note 下的内容块
        const parent = std.store.getParent(block.id);
        return (
          parent &&
          !['affine:note', 'affine:page', 'affine:surface'].includes(
            block.flavour
          )
        );
      });

      if (validBlocks.length === 0) {
        console.warn('No valid blocks found to copy');
        return false;
      }

      console.log(`Copying blocks...${validBlocks.length}`, validBlocks);

      // 创建 BlockSelection 数组
      const blockSelections = validBlocks.map(block =>
        std.selection.create(BlockSelection, {
          blockId: block.id,
        })
      );

      // 设置选区
      std.selection.setGroup('note', blockSelections);

      // 使用命令链复制选中的内容
      const [ok] = std.command
        .chain()
        .pipe(getSelectedModelsCommand)
        .pipe(draftSelectedModelsCommand)
        .with({
          onCopy: () => {
            // 复制完成后清除选中状态
            std.selection.clear(blockSelections.map(it => it.blockId));
          },
        })
        .pipe(copySelectedModelsCommand)
        .run();

      return ok;
    } catch (error) {
      return false;
    }
  }

  __debug_consume_llm_command(md: string) {
    const docId = this.docService.doc?.meta$.getValue().id || '';
    if (!docId) {
      console.error('No document ID found');
      return;
    }

    const uuid = uniqueId();

    this.processChatCommand({
      doc_id: docId,
      uuid: uuid,
      command: [
        {
          doc_id: docId,
          snap_content: md,
          command: 'update',
          uuid,
        },
      ],
    });
  }
}

async function convertSingleBlockToMarkdown(
  host: EditorHost,
  blockId: string
): Promise<string> {
  try {
    // 1. 获取指定的block
    const block = host.std.store.getBlock(blockId);
    if (!block) {
      throw new Error(`Block ${blockId} not found`);
    }

    // 2. 创建transformer
    const transformer = host.std.store.getTransformer();

    // 3. 创建markdown adapter
    const markdownAdapter = new MarkdownAdapter(
      transformer,
      host.std.store.provider
    );

    // 4. 转换单个block（包含所有孙节点）
    const result = await markdownAdapter.fromBlock(block.model);

    if (result) {
      return result.file; // 返回markdown字符串
    }

    throw new Error('Failed to convert block to markdown');
  } catch (error) {
    console.error('Error converting block to markdown:', error);
    throw error;
  }
}
